import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Grid, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, Legend } from 'recharts';
import axios from 'axios';
import config from '../config';

// Colors from spec
const COLORS = {
    green: '#2ECC71',
    orange: '#F39C12',
    red: '#E74C3C',
    blue: '#3498DB',
    grey: '#95A5A6'
};

const RiskAnalysis = () => {
    const [tabValue, setTabValue] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData(tabValue);
    }, [tabValue]);

    const fetchData = async (tabIndex) => {
        setLoading(true);
        try {
            const endpoint = tabIndex === 0 ? 'dashboard/suppliers' : 'dashboard/raw-materials';
            const response = await axios.get(`${config.API_BASE_URL}/risk-analysis/${endpoint}`);
            setData(response.data);
        } catch (err) {
            console.error("Error fetching data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const renderKPICard = (title, value, subtext, color) => (
        <Card sx={{ height: '100%', borderLeft: `6px solid ${color}` }}>
            <CardContent>
                <Typography color="textSecondary" gutterBottom>{title}</Typography>
                <Typography variant="h4" component="div" sx={{ color: color, fontWeight: 'bold' }}>
                    {value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {subtext}
                </Typography>
            </CardContent>
        </Card>
    );

    const renderMatrix = () => (
        <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ color: COLORS.blue }}>
                {tabValue === 0 ? 'Matrice de Risque Fournisseur' : 'Matrice de Risque Réglementaire'}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid />
                    <XAxis type="number" dataKey="probability" name={tabValue === 0 ? "Probabilité" : "Risque Réglementaire"} unit="%" domain={[0, 100]} />
                    <YAxis type="number" dataKey="impact" name={tabValue === 0 ? "Impact" : "Criticité"} unit="%" domain={[0, 100]} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter name="Items" data={data.matrix} fill={COLORS.blue}>
                        {data.matrix.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.riskCategory === 'High' ? COLORS.red : (entry.riskCategory === 'Medium' ? COLORS.orange : COLORS.green)} />
                        ))}
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        </Paper>
    );

    const renderTop15Table = () => (
        <Paper sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
            <Typography variant="h6" gutterBottom sx={{ color: COLORS.blue }}>
                {tabValue === 0 ? 'Top 15 Fournisseurs à Surveiller' : 'Top 15 Matières Premières à Surveiller'}
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rang</TableCell>
                            <TableCell>Nom</TableCell>
                            <TableCell>{tabValue === 0 ? 'Catégorie' : 'Type'}</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>{tabValue === 0 ? 'Raison' : 'Type Risque'}</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.top15.map((row) => (
                            <TableRow key={row.rank}>
                                <TableCell>{row.rank}</TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell><Chip label={tabValue === 0 ? row.category : row.type} size="small" /></TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ width: '100%', mr: 1 }}>
                                            <div style={{ width: `${row.score * 10}%`, height: 6, backgroundColor: row.score > 6 ? COLORS.red : (row.score > 3 ? COLORS.orange : COLORS.green) }} />
                                        </Box>
                                        <Typography variant="body2">{row.score}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{tabValue === 0 ? row.reason : row.riskType}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.status}
                                        size="small"
                                        sx={{
                                            bgcolor: row.status === 'Non-conforme' ? COLORS.red : (row.status === 'Alerte' ? COLORS.orange : COLORS.green),
                                            color: 'white'
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{row.actionRequired}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    const renderDistributionCharts = () => (
        <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: 400 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: COLORS.blue }}>Répartition des Risques</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ bottom: 30 }}>
                            <Pie
                                data={data.distribution.pie}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                                label
                            >
                                {data.distribution.pie.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={[COLORS.green, COLORS.orange, COLORS.red, COLORS.blue, COLORS.grey][index % 5]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: 400 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: COLORS.blue }}>
                        {tabValue === 0 ? 'Évolution du Score Global' : 'Alertes Réglementaires'}
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        {tabValue === 0 ? (
                            <LineChart data={data.distribution.history} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis domain={[0, 10]} />
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} />
                                <Line type="monotone" dataKey="score" stroke={COLORS.blue} strokeWidth={2} name="Score" />
                            </LineChart>
                        ) : (
                            <BarChart data={data.distribution.history} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} />
                                <Bar dataKey="alerts" fill={COLORS.orange} name="Alertes" />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: 400 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: COLORS.blue }}>Géographie des Risques</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={data.distribution.geo} margin={{ top: 20, right: 30, left: 40, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={80} />
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36} />
                            <Bar dataKey="value" fill={COLORS.red} name="Risques Identifiés" />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
        </Grid>
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    if (!data) return <Typography color="error">No data available</Typography>;

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {tabValue === 0 ? 'Tableau de Bord Risque Fournisseur' : 'Tableau de Bord Risque Matières Premières'}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    Cosmétique & Confection de Luxe
                </Typography>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} centered textColor="primary" indicatorColor="primary">
                    <Tab label="Fournisseurs" />
                    <Tab label="Matières Premières" />
                </Tabs>
            </Paper>

            {/* KPIs */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    {renderKPICard('Score de Risque Global', `${data.kpi.globalScore}/10`, 'Moyenne pondérée', data.kpi.globalScore > 6 ? COLORS.red : (data.kpi.globalScore > 3 ? COLORS.orange : COLORS.green))}
                </Grid>
                <Grid item xs={12} md={3}>
                    {renderKPICard(tabValue === 0 ? 'Fournisseurs à Risque Élevé' : 'Matières à Risque Élevé', `${data.kpi.highRisk.count} (${data.kpi.highRisk.percentage}%)`, 'Action immédiate requise', COLORS.red)}
                </Grid>
                <Grid item xs={12} md={3}>
                    {renderKPICard('En Alerte', `${data.kpi.alert.count} (${data.kpi.alert.percentage}%)`, 'Surveillance renforcée', COLORS.orange)}
                </Grid>
                <Grid item xs={12} md={3}>
                    {renderKPICard('Conformes', `${data.kpi.compliant.count} (${data.kpi.compliant.percentage}%)`, 'Monitoring standard', COLORS.green)}
                </Grid>
            </Grid>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Row 1: Matrix (Left) & Table (Right) */}
                <Grid item xs={12} md={5}>
                    {renderMatrix()}
                </Grid>
                <Grid item xs={12} md={7}>
                    {renderTop15Table()}
                </Grid>

                {/* Row 2: Distribution Charts */}
                <Grid item xs={12}>
                    {renderDistributionCharts()}
                </Grid>
            </Grid>
        </Box>
    );
};

export default RiskAnalysis;
